import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Loader2, Filter, X } from 'lucide-react';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useView } from '../../contexts/ViewContext';
import {
  scheduledDashboardAPI,
  type ScheduledDashboardData,
} from '../../lib/api/scheduled-dashboard';
import { coreAPI } from '../../lib/api';
import { CurrentWeek } from './scheduled/CurrentWeek';
import { PerWeek } from './scheduled/PerWeek';
import { YTD } from './scheduled/YTD';
import { Forecast } from './scheduled/Forecast';
import SearchableSelect from '../ui/SearchableSelect';

type SubTab = 'current_week' | 'per_week' | 'ytd' | 'forecast';

const ALL_SUB_TABS: Array<{ id: SubTab; label: string; sub: string }> = [
  { id: 'current_week', label: 'Current Week', sub: 'this week' },
  { id: 'per_week',     label: 'Per Week',     sub: '12-week trend' },
  { id: 'ytd',          label: 'Year to Date', sub: 'Jan 1 → today' },
  { id: 'forecast',     label: 'Forecast',     sub: 'today → ∞' },
];

interface CustomerOption { id: number; name: string }

export interface ScheduledShellProps {
  /**
   * ERP order type to scope all data:
   *   '4'   — scheduling-agreement orders (Scheduled Dashboards tab)
   *   '1'   — spot / one-off orders (Spot Orders tab)
   *   'all' — every PO line, no ERP-type filter (Sales Orders tab)
   */
  erpOrderType: '4' | '1' | 'all';
  /** Visible scope statement shown above the sub-tabs. */
  scopeNotice: string;
  /**
   * Whether to expose the Forecast sub-tab. Forecast is meaningful for
   * scheduling agreements (firm window per part); spot/one-off orders
   * have no firm window so the tab is hidden by default for them.
   */
  showForecast?: boolean;
  /** Loading message shown while the dashboard payload is in flight. */
  loadingLabel?: string;
}

/**
 * Shared shell for both the Scheduled Dashboards (ERP type 4) and Spot Orders
 * (ERP type 1) tabs. Owns: filter state, customers fetch, dashboard fetch,
 * sub-tab control, scope notice, and dispatch into period sub-components.
 */
export function ScheduledShell({
  erpOrderType,
  scopeNotice,
  showForecast = true,
  loadingLabel = 'Loading KPIs…',
}: ScheduledShellProps) {
  const { selectedCompanyId, selectedSiteId, viewKey } = useView();
  const { formatCurrency } = useFormatters();
  // A site-restricted helix admin holds selectedCompanyId=null and switches
  // scope via selectedSiteId (site id == supplier-company id here). Resolve
  // from whichever is set so the fetch re-scopes + refetches on any switch.
  const scopeSupplierId = selectedCompanyId ?? selectedSiteId;

  const [data, setData] = useState<ScheduledDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SubTab>('current_week');

  const subTabs = useMemo(
    () => (showForecast ? ALL_SUB_TABS : ALL_SUB_TABS.filter((t) => t.id !== 'forecast')),
    [showForecast],
  );

  // Filter state — committed values are what's sent to the API. The part
  // input auto-commits 400ms after the user stops typing, so they don't have
  // to remember to press Enter. Enter still commits immediately; Esc clears.
  const [customerId, setCustomerId] = useState<string>('');
  const [partInput, setPartInput] = useState<string>('');
  const [partCommitted, setPartCommitted] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => setPartCommitted(partInput.trim()), 400);
    return () => clearTimeout(t);
  }, [partInput]);

  const [customers, setCustomers] = useState<CustomerOption[]>([]);

  // Load customer list once via the portal-aware /core/companies/for-filter/
  // action (scoped per tenant via CustomerSupplierRelationship). The Helix-only
  // /core/companies/ list 403s for non-Helix users. Failure is non-fatal.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let all: Array<{ id: number; name: string }> = [];
      try {
        const resp = await coreAPI.getCustomerSitesForFilter();
        if (cancelled) return;
        all = resp.data.results || [];
      } catch { /* dropdown stays empty */ }
      if (cancelled) return;
      setCustomers(
        all
          .map((c) => ({ id: c.id, name: c.name || `Customer ${c.id}` }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    })();
    return () => { cancelled = true; };
  }, []);

  // Dashboard fetch — refires when scope or filters change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: Record<string, string | number> = { erp_order_type: erpOrderType };
    if (scopeSupplierId) params['supplier_id'] = Number(scopeSupplierId);
    if (customerId) params['customer_id'] = Number(customerId);
    if (partCommitted) params['part_number'] = partCommitted;

    scheduledDashboardAPI
      .get(params)
      .then((resp) => { if (!cancelled) setData(resp.data); })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load dashboard';
          setError(msg);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scopeSupplierId, viewKey, erpOrderType, customerId, partCommitted]);

  // If Forecast was the active tab and the shell is reused for Spot Orders
  // (or showForecast flips off), drop back to Current Week.
  useEffect(() => {
    if (!showForecast && activeTab === 'forecast') setActiveTab('current_week');
  }, [showForecast, activeTab]);

  const activeFilters = useMemo(() => {
    const labels: string[] = [];
    if (customerId) {
      const name = customers.find((s) => String(s.id) === customerId)?.name ?? `Customer ${customerId}`;
      labels.push(`Customer: ${name}`);
    }
    if (partCommitted) labels.push(`Part: ${partCommitted}`);
    return labels;
  }, [customerId, partCommitted, customers]);

  const clearFilters = () => {
    setCustomerId('');
    setPartInput('');
    setPartCommitted('');
  };

  // Filter bar always renders so the user can adjust scope even on error.
  const filterBar = (
    <div
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
      role="group"
      aria-label="Dashboard filters"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <Filter className="h-3.5 w-3.5" />
          <span>Filters</span>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="sd-customer" className="text-xs text-slate-500">Customer</label>
          <div className="w-96">
            <SearchableSelect
              options={customers.map((s) => ({ value: String(s.id), label: s.name }))}
              value={customerId}
              onChange={setCustomerId}
              placeholder="All customers"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="sd-part" className="text-xs text-slate-500">Part</label>
          <input
            id="sd-part"
            type="text"
            value={partInput}
            onChange={(e) => setPartInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setPartCommitted(partInput.trim());
              if (e.key === 'Escape') { setPartInput(''); setPartCommitted(''); }
            }}
            placeholder="type part # — applies as you type"
            title="Auto-applies after you stop typing. Esc to clear."
            className="h-8 w-56 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
        </div>

        {activeFilters.length > 0 && (
          <button
            onClick={clearFilters}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 hover:border-red-400"
          >
            <X className="h-3.5 w-3.5" /> Clear filters
          </button>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        <span>{scopeNotice}</span>
        {activeFilters.length > 0 && (
          <>
            <span className="text-slate-300">•</span>
            <span className="text-slate-600">Filtered by {activeFilters.join(' · ')}</span>
          </>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {filterBar}
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">{loadingLabel}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {filterBar}
        <Card>
          <CardContent className="py-8 text-center text-destructive">{error}</CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {filterBar}

      <div className="border-b border-slate-200" role="tablist" aria-label="Time period">
        <div className="flex gap-1">
          {subTabs.map((t) => {
            const active = t.id === activeTab;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                aria-current={active ? 'page' : undefined}
                onClick={() => setActiveTab(t.id)}
                className={`px-5 py-2.5 text-sm relative transition-colors ${
                  active ? 'text-slate-900 font-semibold' : 'text-slate-500 font-medium hover:text-slate-800'
                }`}
              >
                <span>{t.label}</span>
                <span className="ml-2 text-[11px] font-normal text-slate-400">{t.sub}</span>
                {active && (
                  <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-slate-900" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {activeTab === 'current_week' && <CurrentWeek data={data} formatCurrency={formatCurrency} />}
        {activeTab === 'per_week'     && <PerWeek     data={data} formatCurrency={formatCurrency} />}
        {activeTab === 'ytd'          && <YTD         data={data} formatCurrency={formatCurrency} />}
        {activeTab === 'forecast' && showForecast && <Forecast data={data} formatCurrency={formatCurrency} />}
      </div>
    </div>
  );
}

/** Scheduling-agreement view (ERP type 4). */
export function ScheduledTab() {
  return (
    <ScheduledShell
      erpOrderType="4"
      scopeNotice="Showing scheduling-agreement orders only. Spot / one-off orders are on the Spot Orders tab."
      loadingLabel="Loading scheduled KPIs…"
      showForecast
    />
  );
}
